import { createFeatureSelector, createSelector } from '@ngrx/store';
import { greaterThanEqual, greaterThanZero, lessThanZero } from '../../shared/helpers/number.helper';
import type { SalesYear } from '../../shared/models/sales-year.type';
import type { SalesYearsState } from '../reducers/sales-years.reducer';
import { salesYearsStateKey } from '../reducers/sales-years.reducer';
import { getRouterQueryParams } from './router.selector';

export const getSalesYearsState = createFeatureSelector<SalesYearsState>(salesYearsStateKey);

// Users can only see sales years back to 5 years in the past
export const getSalesYears = createSelector(getSalesYearsState, (state: SalesYearsState) =>
  state.salesYears.filter((sy) => sy.yearsFromCurrentYear >= -5)
);
export const getSalesYearsLoaded = createSelector(
  getSalesYearsState,
  (state: SalesYearsState) => state.salesYearsLoaded
);
export const getSalesYearsLoading = createSelector(
  getSalesYearsState,
  (state: SalesYearsState) => state.salesYearsLoading
);

export const selectSelectedSalesYear = createSelector(
  getSalesYearsState,
  (state: SalesYearsState) => state.selectedSalesYear
);
export const selectSalesYearsChanging = createSelector(
  getSalesYearsState,
  (state: SalesYearsState) => state.salesYearChanging
);
export const selectSelectedSalesYearIsCurrent = createSelector(selectSelectedSalesYear, (selectedSalesYear) => {
  return Boolean(selectedSalesYear?.isCurrentYear);
});

export const getCurrentSalesYear = createSelector(getSalesYears, (salesYears) => {
  const currentSalesYear = salesYears.find((year: SalesYear) => {
    return year.isCurrentYear;
  });
  return currentSalesYear;
});
export const getPreviousSalesYear = createSelector(getSalesYears, (salesYears) => {
  const lastYear = salesYears.find((year: SalesYear) => {
    return year.yearsFromCurrentYear === -1;
  });
  return lastYear;
});
export const getNextSalesYear = createSelector(getSalesYears, (salesYears) => {
  const nextYear = salesYears.find((year: SalesYear) => {
    return year.yearsFromCurrentYear === 1;
  });
  return nextYear;
});

export const selectCurrentSalesYearCode = createSelector(getCurrentSalesYear, (currentSalesYear) => {
  if (currentSalesYear === undefined) {
    return null;
  }

  return currentSalesYear.code;
});

export const getSalesYearFromSalesYearCode = (salesYearCode: string) => {
  return createSelector(getSalesYears, (salesYears) => {
    return salesYears.find((sy) => sy.code === salesYearCode);
  });
};

export const selectSalesYearQueryParamObject = createSelector(getRouterQueryParams, (params) => {
  return params?.yearCode ? { salesYear: params.yearCode as string } : undefined;
});

export const selectYearCodeQueryParamObject = createSelector(getRouterQueryParams, (params) => {
  return params?.yearCode ? { yearCode: params.yearCode as string } : undefined;
});
export const selectYearCodeQueryParam = createSelector(getRouterQueryParams, (params): string | undefined => {
  return params?.yearCode;
});

export const selectSalesYearFromRoute = createSelector(
  selectYearCodeQueryParam,
  getSalesYears,
  (yearCodeParam, salesYears) => {
    return salesYears.find((salesYear) => salesYear.code === yearCodeParam);
  }
);

export const selectSalesYearFromRouteIsValidSalesYear = createSelector(selectSalesYearFromRoute, (salesYear) => {
  return Boolean(salesYear);
});

export const selectSalesYearFromRouteIsCurrentSalesYear = createSelector(selectSalesYearFromRoute, (salesYear) => {
  return Boolean(salesYear?.isCurrentYear);
});

export const selectSalesYearFromRouteNotCurrentSalesYear = createSelector(
  selectSalesYearFromRouteIsCurrentSalesYear,
  (isCurrentSalesYear) => {
    return isCurrentSalesYear === false;
  }
);

export const selectSalesYearFromRouteIsLastSalesYear = createSelector(selectSalesYearFromRoute, (salesYear) => {
  return salesYear?.yearsFromCurrentYear === -1;
});

export const selectIsSalesYearFromRouteInPast = createSelector(selectSalesYearFromRoute, (salesYear) =>
  lessThanZero(salesYear?.yearsFromCurrentYear)
);

export const selectIsSalesYearFromRouteInFuture = createSelector(selectSalesYearFromRoute, (salesYear) =>
  greaterThanZero(salesYear?.yearsFromCurrentYear)
);

export const selectIsSalesYearFromRouteCurrentOrFuture = createSelector(selectSalesYearFromRoute, (salesYear) =>
  greaterThanEqual(salesYear?.yearsFromCurrentYear, 0)
);

export const getCurrentSalesYearDateRange = createSelector(getCurrentSalesYear, (currentSalesYear) => {
  if (currentSalesYear === undefined) {
    return null;
  }

  const { startYear, endYear } = currentSalesYear;
  return { startDate: new Date(`${startYear}-08-02`), endDate: new Date(`${endYear}-08-01`) };
});
